'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

interface SideDrawerProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  container?: HTMLElement;
  onModalClick?: () => void;
}

export default function SideDrawer({ visible, onClose, children, container, onModalClick }: SideDrawerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // 处理点击外部区域关闭
  // useEffect(() => {
  //   if (!visible || !shouldRender) return;

  //   const handleClickOutside = (e: MouseEvent) => {
  //     const target = e.target as HTMLElement;

  //     // 如果点击的是入口按钮或其子元素，不关闭
  //     if (target.closest('[data-user-center-entry]')) {
  //       return;
  //     }

  //     // 如果点击的目标不在内容区域内，就关闭
  //     if (contentRef.current && !contentRef.current.contains(target)) {
  //       onClose();
  //     }
  //   };

  //   const timer = setTimeout(() => {
  //     document.body.addEventListener('click', handleClickOutside);
  //   }, 10);

  //   return () => {
  //     clearTimeout(timer);
  //     document.body.removeEventListener('click', handleClickOutside);
  //   };
  // }, [visible, shouldRender, onClose]);

  // 处理 ESC 键关闭
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [visible, onClose]);

  // 阻止背景滚动
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  // 控制动画和渲染
  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      // 延迟触发动画，确保 DOM 已经渲染
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // 等待动画结束后再卸载
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  if (!shouldRender) return null;

  const handleModalClick = () => {
    onModalClick?.();
  };

  const drawerContent = (
    <div className="fixed inset-0 right-0 top-0 h-full z-[48]">
      {/* 遮罩层 */}
      <div
        id="sideDrawerModalClick"
        onClick={handleModalClick}
        className={clsx(
          'absolute inset-0 bg-black/85 transition-opacity duration-300',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
      />

      {/* 内容区域 - 占80%宽度 */}
      <div
        ref={contentRef}
        className={clsx(
          'absolute right-0 top-0 h-full w-[80%] max-w-[500px] min-w-[316px] shadow-xl',
          'transform transition-transform duration-300 ease-out',
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {children}
      </div>
    </div>
  );

  // 如果指定了容器，使用 Portal 挂载到容器，否则挂载到 body
  return container ? createPortal(drawerContent, container) : createPortal(drawerContent, document.body);
}

